import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

/**
 * Global page-load progress bar — similar to YouTube / GitHub.
 * Hooks into axios interceptors to track active API calls.
 * Fills gradually while requests are in-flight, then snaps to 100% on completion.
 */

let activeRequests = 0;
let totalRequests = 0;
let listeners = [];

function notify() {
  listeners.forEach(fn => fn({ active: activeRequests, total: totalRequests }));
}

// Install axios interceptors once
let interceptorsInstalled = false;
function installInterceptors() {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  axios.interceptors.request.use(config => {
    activeRequests++;
    totalRequests++;
    notify();
    return config;
  }, error => {
    return Promise.reject(error);
  });

  axios.interceptors.response.use(response => {
    activeRequests = Math.max(0, activeRequests - 1);
    if (activeRequests === 0) totalRequests = 0;
    notify();
    return response;
  }, error => {
    activeRequests = Math.max(0, activeRequests - 1);
    if (activeRequests === 0) totalRequests = 0;
    notify();
    return Promise.reject(error);
  });
}

export const GlobalProgressBar = () => {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);
  const trickleRef = useRef(null);

  useEffect(() => {
    installInterceptors();

    const handler = ({ active, total }) => {
      if (active > 0) {
        setVisible(true);
        // Calculate base progress from completed requests
        const completed = total - active;
        const base = total > 0 ? (completed / total) * 90 : 0;
        setProgress(Math.max(base, 10));

        // Trickle effect — slowly increment while waiting
        clearInterval(trickleRef.current);
        trickleRef.current = setInterval(() => {
          setProgress(prev => {
            if (prev >= 90) return prev;
            return prev + (90 - prev) * 0.04;
          });
        }, 300);
      } else {
        // All done — snap to 100% then fade out
        clearInterval(trickleRef.current);
        setProgress(100);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          setVisible(false);
          setProgress(0);
        }, 500);
      }
    };

    listeners.push(handler);
    return () => {
      listeners = listeners.filter(fn => fn !== handler);
      clearInterval(trickleRef.current);
      clearTimeout(timerRef.current);
    };
  }, []);

  if (!visible && progress === 0) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] h-[2.5px] pointer-events-none"
      data-testid="global-progress-bar"
      style={{ opacity: visible ? 1 : 0, transition: "opacity 0.4s ease" }}
    >
      <div
        className="h-full rounded-r-full"
        style={{
          width: `${progress}%`,
          background: "linear-gradient(90deg, #06b6d4, #8b5cf6, #ec4899)",
          transition: progress === 100 ? "width 0.2s ease" : "width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "0 0 8px rgba(139, 92, 246, 0.5), 0 0 4px rgba(6, 182, 212, 0.3)",
        }}
      />
    </div>
  );
};
